import {NgTemplateOutlet} from "@angular/common"
import {Component, computed, DestroyRef, inject, Injector, input, OnInit, output, signal, ChangeDetectionStrategy} from "@angular/core"
import {CM_LOGGER_TEMPLATES} from "@common/services/logging"
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop"
import {MatMenuModule} from "@angular/material/menu"
import {MatTooltipModule} from "@angular/material/tooltip"
import {RouterLink} from "@angular/router"
import {NotificationsService} from "@app/common/services/notifications/notifications.service"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {ImageProcessingService} from "@app/common/services/rendering/image-processing.service"
import {RenderingService} from "@app/common/services/rendering/rendering.service"
import {
    createRenderJob,
    getAssignmentKey,
    getConfigurationString,
    getJobAssignments,
    getPostProcessingInput,
    isJobError,
    isJobPending,
    JobData,
    postProcessingSettingsToImageProcessingSettings,
} from "@app/template-editor/helpers/template-jobs"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {ImageProcessingNodes, PostProcessingInputData} from "@cm/image-processing-nodes"
import {postProcessingGraph} from "@cm/image-processing/operations/render-post-processing"
import {DEFAULT_RENDER_QUEUE_GROUP, PictureRenderJobOutputSchema} from "@cm/job-nodes/rendering"
import {SceneNodes} from "@cm/template-nodes"
import {hashObject} from "@cm/utils"
import {jsonToGraph} from "@cm/browser-utils"
import {ButtonComponent} from "@common/components/buttons/button/button.component"
import {LoadingSpinnerComponent, LoadingSpinnerIconComponent} from "@common/components/progress"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {LocalStorageService} from "@common/services/local-storage/local-storage.service"
import {MatSelectModule} from "@angular/material/select"
import {MatFormFieldModule} from "@angular/material/form-field"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {ImageColorSpace, JobState} from "@generated"
import {
    CancelJobForTemplateImageViewerGQL,
    DiscardJobTaskForTemplateImageViewerGQL,
    GetJobDetailsForTemplateImageViewerGQL,
    GetJobsForTemplateImageViewerGQL,
    GetRenderQueueGroupsForTemplateImageViewerGQL,
    JobDetailsForTemplateImageViewerFragment,
} from "@template-editor/components/template-image-viewer/template-image-viewer.component.generated"
import {TemplateJobManagerService} from "@template-editor/services/template-job-manager.service"
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    defer,
    EMPTY,
    filter,
    firstValueFrom,
    from,
    map,
    of,
    switchMap,
    takeWhile,
    tap,
    distinctUntilChanged,
} from "rxjs"
import {TemplateImageViewerCanvasComponent} from "@app/template-editor/components/template-image-viewer-canvas/template-image-viewer-canvas.component"
import {TemplateImageViewerControlsComponent} from "@app/template-editor/components/template-image-viewer-controls/template-image-viewer-controls.component"
import {TemplateTreeViewType} from "@app/template-editor/components/template-tree-view-type-selector/template-tree-view-type-selector.component"
import {ActionItemComponent} from "@app/common/components/menu/actions/action-item/action-item.component"
import {ImageProcessingUtils} from "@cm/image-processing/image-processing"
import {environment} from "@environment"
import {SceneManagerCacheService} from "@app/template-editor/services/scene-manager-cache.service"

const JOB_POLL_INTERVAL = 5000 // 5 seconds

export type PostProcessedImageData = {
    imageData: ImageData
    rawImageData: PostProcessingInputData
}
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-image-viewer",
    templateUrl: "./template-image-viewer.component.html",
    styleUrl: "./template-image-viewer.component.scss",
    providers: [SceneManagerService],
    imports: [
        LoadingSpinnerComponent,
        TemplateImageViewerCanvasComponent,
        TemplateImageViewerControlsComponent,
        ButtonComponent,
        MatTooltipModule,
        LoadingSpinnerIconComponent,
        ActionItemComponent,
        MatMenuModule,
        RouterLink,
        NgTemplateOutlet,
        MatSelectModule,
        MatFormFieldModule,
    ],
})
export class TemplateImageViewerComponent implements OnInit {
    readonly $maskOverlayColor = input<ImageProcessingNodes.RGBColor>([1, 0.2, 0.2], {alias: "maskOverlayColor"})
    readonly $maskOverlayAlpha = input(0.5, {alias: "maskOverlayAlpha"})
    readonly $fileTitle = input<string>(undefined, {alias: "fileTitle"})
    readonly $viewType = input.required<TemplateTreeViewType>({alias: "viewType"})

    readonly $sceneManagerService = input.required<SceneManagerService>({alias: "sceneManagerService"})
    private readonly sceneManagerCacheService = inject(SceneManagerCacheService)
    private downloadAbortController: AbortController | null = null
    private readonly localSceneManagerService = inject(SceneManagerService)
    private readonly injector = inject(Injector)
    private readonly destroyRef = inject(DestroyRef)
    private readonly renderingService = inject(RenderingService)
    private readonly notificationsService = inject(NotificationsService)
    private readonly templateJobManagerService = inject(TemplateJobManagerService)
    private readonly getJobDetailsForTemplateImageViewerGQL = inject(GetJobDetailsForTemplateImageViewerGQL)
    private readonly cancelJobGql = inject(CancelJobForTemplateImageViewerGQL)
    private readonly discardJobTaskGql = inject(DiscardJobTaskForTemplateImageViewerGQL)
    private readonly jobsGql = inject(GetJobsForTemplateImageViewerGQL)
    private readonly renderQueueGroupsGql = inject(GetRenderQueueGroupsForTemplateImageViewerGQL)
    private readonly localStorageService = inject(LocalStorageService)

    readonly developmentMode = this.localStorageService.developmentMode
    readonly $renderQueueGroups = signal<string[]>([])
    // mat-select treats a null option value as "nothing selected" and clears itself, so cloud rendering
    // is the empty string rather than null
    readonly $renderQueueGroup = signal<string>("")
    private readonly logger = inject(CM_LOGGER_TEMPLATES)
    readonly $can = inject(PermissionsService).$to

    private readonly $postProcessingSettings = computed(() => this.$sceneManagerService().$scene().find(SceneNodes.RenderPostProcessingSettings.is))

    private resolvedProcessingSettings$ = toObservable(this.$postProcessingSettings).pipe(
        map((settings) => (settings ? postProcessingSettingsToImageProcessingSettings(settings) : undefined)),
        switchMap((settings) => {
            if (!settings) return of(undefined)

            function isColorTuple(x: ImageProcessingNodes.RGBColor | ImageProcessingNodes.ImageNode | undefined): x is ImageProcessingNodes.RGBColor {
                return Array.isArray(x) && x.length === 3 && x.every((num) => typeof num === "number")
            }

            const {backgroundColor, mask, ...rest} = settings

            const resolveImage = (image: ImageProcessingNodes.ImageNode) =>
                defer(() =>
                    from(
                        ImageProcessingUtils.resolveExternalData(image, async (node) => {
                            if (node.sourceData.type !== "dataObjectReference") throw new Error("Unsupported source type")
                            if (typeof node.resolvedData !== "undefined") return
                            if (node.resolveTo !== "encodedData") throw new Error(`Cannot resolve external data type: ${node.resolveTo}`)

                            const {dataObject, dataObjectLegacy} = this.sceneManagerCacheService.dataResolverService.resolve
                            const {dataObjectId} = node.sourceData

                            const resolvedDataObject = await (typeof dataObjectId === "number" ? dataObjectLegacy(dataObjectId) : dataObject(dataObjectId))

                            const resp = await firstValueFrom(this.sceneManagerCacheService.dataObjectContentCache.evaluateDataObject(resolvedDataObject))

                            node.resolvedData = {
                                data: resp,
                                mediaType: resolvedDataObject.mediaType,
                                colorSpace: resolvedDataObject.imageColorSpace === ImageColorSpace.Srgb ? "sRGB" : "linear",
                            }
                        }),
                    ),
                )

            return combineLatest([
                isColorTuple(backgroundColor) || !backgroundColor ? of(backgroundColor) : resolveImage(backgroundColor),
                mask ? resolveImage(mask) : of(undefined),
            ]).pipe(map(([backgroundColor, mask]) => ({...rest, backgroundColor, mask})))
        }),
    )
    private maskOverlayColor$ = toObservable(this.$maskOverlayColor)
    private maskOverlayAlpha$ = toObservable(this.$maskOverlayAlpha)

    readonly $renderSettings = computed(() => this.$sceneManagerService().$scene().find(SceneNodes.RenderSettings.is))
    readonly $camera = computed(() => this.$sceneManagerService().$scene().find(SceneNodes.Camera.is))

    readonly viewTypeChanged = output<TemplateTreeViewType>()

    readonly $stage = signal<"init" | "waitingRenderOutput" | "downloading" | "processing" | "done">("init")
    readonly $renderJobData = signal<JobData | undefined>(undefined)
    readonly $result = signal<{error: {description: string}} | PostProcessedImageData | undefined>(undefined)
    readonly $startingRenderJob = signal(false)
    readonly $cancellingRenderJob = signal(false)
    readonly $deletingRenderJob = signal(false)

    $overrideRenderJobData = this.templateJobManagerService.$selectedUnassignedJob
    overrideRenderJobData$ = toObservable(this.$overrideRenderJobData)

    readonly $currentConfiguration = computed(() => this.$sceneManagerService().$currentLocalConfiguration())
    currentConfiguration$ = toObservable(this.$currentConfiguration)

    JobState = JobState
    readonly $isJobPending = signal(isJobPending)
    readonly $isJobError = signal(isJobError)

    private refreshJobs$ = new BehaviorSubject<void>(undefined)

    refreshJobs() {
        this.refreshJobs$.next()
    }

    private readonly imageProcessingService = inject(ImageProcessingService)

    isError = (result: unknown): result is {error: {description: string}} => {
        if (result === null || typeof result !== "object") return false
        if (!("error" in result)) return false
        return typeof result.error !== "undefined"
    }

    ngOnInit() {
        this.templateJobManagerService.jobsDiscarded$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.refreshJobs())
        this.localSceneManagerService.$lodType.set("pathTraced")
        this.localSceneManagerService.$computeDummyReifiedMeshData.set(true)

        if (this.developmentMode) void this.loadRenderQueueGroups()

        const handleError = (error: unknown) => {
            return of({error: {description: error instanceof Error ? error.message : String(error)}})
        }

        const settings$ = combineLatest([this.resolvedProcessingSettings$, this.maskOverlayColor$, this.maskOverlayAlpha$]).pipe(
            map(([postProcessingSettings, maskOverlayColor, maskOverlayAlpha]) => ({postProcessingSettings, maskOverlayColor, maskOverlayAlpha})),
        )

        const jobChanged$ = combineLatest([
            this.overrideRenderJobData$,
            this.$sceneManagerService().templateRevisionId$,
            this.currentConfiguration$,
            this.refreshJobs$,
        ])

        jobChanged$
            .pipe(
                tap(() => {
                    this.$stage.set("init")
                    this.$renderJobData.set(undefined)
                }),
                switchMap(([overrideRenderJobData, templateRevisionId, configuration]) => {
                    if (overrideRenderJobData) return of(overrideRenderJobData.job)

                    if (!templateRevisionId) return handleError(Error("No template revision id set"))

                    const configurationString = getConfigurationString(configuration)
                    return from(getJobAssignments(this.injector, templateRevisionId, [getAssignmentKey("render", configurationString)])).pipe(
                        map(([renderJob]) => {
                            if (!renderJob) throw Error("No render job found for the current configuration")
                            return renderJob
                        }),
                        catchError(handleError),
                    )
                }),
                tap((renderJob) => {
                    if (this.isError(renderJob)) return
                    this.$renderJobData.set(renderJob)
                }),
                switchMap((renderJob) => {
                    if (this.isError(renderJob)) return of(renderJob)
                    if (isJobPending(renderJob)) {
                        return this.getJobDetailsForTemplateImageViewerGQL
                            .watch({id: renderJob.id}, {pollInterval: JOB_POLL_INTERVAL, fetchPolicy: "no-cache"})
                            .valueChanges.pipe(
                                map(({data}): JobDetailsForTemplateImageViewerFragment => data.job),
                                takeWhile((job) => isJobPending(job), true),
                                catchError(handleError),
                            )
                    }
                    return of(renderJob)
                }),
                switchMap((renderJob) => {
                    try {
                        if (this.isError(renderJob)) return of(renderJob)
                        if (isJobError(renderJob)) throw Error(`Render job failed with state ${renderJob.state} and message: ${renderJob.message}`)
                        else if (isJobPending(renderJob) || renderJob.state === JobState.Complete) {
                            const {output} = renderJob
                            const graph = jsonToGraph(output)
                            const renderOutput = PictureRenderJobOutputSchema.parse(graph)
                            return of({renderJob, renderOutput})
                        } else throw Error(`Render job in unexpected state ${renderJob.state}`)
                    } catch (error) {
                        return handleError(error)
                    }
                }),
            )
            .pipe(
                tap((data) => {
                    if (this.isError(data)) return
                    const {renderJob} = data
                    this.$renderJobData.set(renderJob)
                }),
                switchMap((data) => {
                    if (this.isError(data)) return of(data)
                    const {renderJob, renderOutput} = data
                    if (!renderOutput || (!renderOutput.renderPasses && !renderOutput.preview)) {
                        if (isJobPending(renderJob)) {
                            this.$stage.set("waitingRenderOutput")
                            return EMPTY
                        } else return handleError(Error("Render output has no render passes or preview"))
                    }
                    return of({renderJob, renderOutput})
                }),
                distinctUntilChanged((prev, curr) => {
                    if (this.isError(curr) || this.isError(prev)) return false
                    return hashObject(prev.renderOutput) === hashObject(curr.renderOutput)
                }),
                tap(() => {
                    // Abort any previous download before starting a new one
                    if (this.downloadAbortController) {
                        this.downloadAbortController.abort()
                    }
                    this.downloadAbortController = new AbortController()
                    this.$stage.set("downloading")
                }),
                switchMap((data) => {
                    if (this.isError(data)) return of(data)
                    const {renderJob, renderOutput} = data
                    const abortSignal = this.downloadAbortController!.signal
                    return from(getPostProcessingInput(renderOutput, this.injector, true, abortSignal)).pipe(
                        map((postProcessingInput) => ({renderJob, postProcessingInput})),
                        catchError(handleError),
                    )
                }),
                switchMap((data) => {
                    if (this.isError(data)) return of(data)
                    const {postProcessingInput} = data

                    return settings$.pipe(
                        tap(() => {
                            this.$stage.set("processing")
                        }),
                        switchMap(({postProcessingSettings, maskOverlayColor, maskOverlayAlpha}) => {
                            const {image, selectedMask: mask} = postProcessingGraph(postProcessingInput, postProcessingSettings ?? {})
                            let graph = image

                            if (mask) {
                                graph = {
                                    type: "blend",
                                    mode: "normal",
                                    amount: maskOverlayAlpha,
                                    background: graph,
                                    foreground: {
                                        type: "applyMask",
                                        input: {
                                            type: "sRGB",
                                            color: maskOverlayColor,
                                        },
                                        mask,
                                    },
                                }
                            }
                            graph = {
                                type: "convert",
                                dataType: "uint8",
                                channelLayout: "RGBA",
                                sRGB: true,
                                input: graph,
                            }

                            return this.imageProcessingService.evalGraph(graph).pipe(catchError(handleError))
                        }),
                        switchMap((processedImage) => {
                            if (this.isError(processedImage)) return of(processedImage)
                            return this.imageProcessingService.toCanvasImageData(processedImage.image)
                        }),
                        filter((imageData): imageData is ImageData => {
                            if (imageData === null) throw new Error("Image data is null")
                            return true
                        }),
                        map((imageData) => ({imageData, rawImageData: postProcessingInput})),
                        catchError(handleError),
                    )
                }),
                catchError(handleError),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((result) => {
                this.$stage.set("done")
                this.$result.set(result)
            })
    }

    private async loadRenderQueueGroups() {
        try {
            const {jobTaskTypes} = await fetchThrowingErrors(this.renderQueueGroupsGql)({filter: {taskType: {equals: "Rendering.cmRender"}}})
            // the default is already offered as "Cloud rendering", and selecting it would route identically
            const queueGroups = Array.from(
                new Set(jobTaskTypes.flatMap((taskType) => (taskType && taskType.queueGroup !== DEFAULT_RENDER_QUEUE_GROUP ? [taskType.queueGroup] : []))),
            ).sort()
            this.$renderQueueGroups.set(queueGroups)
        } catch (error) {
            this.logger.warn("Could not load the render queue groups", error)
        }
    }

    async render() {
        try {
            this.$startingRenderJob.set(true)

            const templateRevisionId = this.$sceneManagerService().$templateRevisionId()
            if (!templateRevisionId) throw Error("No template revision id set")

            const defaultCustomerId = this.$sceneManagerService().$defaultCustomerId()
            if (!defaultCustomerId) throw Error("No default customer id set")

            const runningJobs = await fetchThrowingErrors(this.jobsGql)({
                filter: {
                    organizationLegacyId: {equals: defaultCustomerId},
                    state: [JobState.Init, JobState.Runnable, JobState.Running],
                },
            })

            if (runningJobs.jobs.length > environment.rendering.maxConcurrentRenderJobs)
                throw new Error("Too many concurrent render jobs. Please wait for existing jobs to complete before starting a new one.")

            this.localSceneManagerService.$templateRevisionId.set(templateRevisionId)
            this.localSceneManagerService.$defaultCustomerId.set(defaultCustomerId)

            const clonedConfiguration = this.$currentConfiguration().clone({cloneSubNode: () => true})
            const clonedTemplateGraph = this.$sceneManagerService()
                .$templateGraph()
                .clone({cloneSubNode: () => true})
            this.localSceneManagerService.$templateGraph.set(clonedTemplateGraph)
            this.localSceneManagerService.$instanceParameters.set(clonedConfiguration)

            this.localSceneManagerService.compileTemplate()
            await this.localSceneManagerService.sync(true)

            const variationString = getConfigurationString(clonedConfiguration)

            const [, renderJob] = await createRenderJob(
                this.injector,
                this.renderingService,
                templateRevisionId,
                variationString,
                this.localSceneManagerService.$scene(),
                defaultCustomerId,
                this.$renderQueueGroup() || undefined,
            )

            this.notificationsService.showInfo(`Job submitted. (id = ${renderJob.id})`)
        } catch (error) {
            this.notificationsService.showError(`Failed to start render job: ${(error as Error).message}`)
            this.logger.error("Failed to start render job", error)
        } finally {
            this.$startingRenderJob.set(false)
        }

        this.refreshJobs()
    }

    async reRender() {
        // Retire the current render first so its slot is free, then submit the new one. If retiring
        // fails, stop: the slot is still taken, and the error has already been shown.
        if (!(await this.discardRenderJob())) return
        await this.render()
    }

    // Stop a running render. A plain cancel, not a discard: a render that finished meanwhile is
    // refused by the backend and kept, and a cancelled render can still be restarted.
    async cancelRenderJob() {
        this.$cancellingRenderJob.set(true)
        try {
            const job = this.$overrideRenderJobData()?.job ?? this.$renderJobData()
            if (job) await mutateThrowingErrors(this.cancelJobGql)({id: job.id})
        } catch (error) {
            this.notificationsService.showError(`Failed to cancel render job: ${(error as Error).message}`)
            this.logger.error("Failed to cancel render job", error)
        } finally {
            this.$cancellingRenderJob.set(false)
            this.refreshJobs()
        }
    }

    // Retire the shown render (assigned or unassigned) instead of deleting it: the job stays on
    // record and its slot frees. Returns whether the slot is free afterwards — also true when there
    // was nothing to retire. On failure the view is left as it was, since the job is still there.
    async discardRenderJob(): Promise<boolean> {
        this.$deletingRenderJob.set(true)
        const override = this.$overrideRenderJobData()
        try {
            const job = override?.job ?? this.$renderJobData()
            if (job) await mutateThrowingErrors(this.discardJobTaskGql)({id: job.id})
            if (override)
                this.$overrideRenderJobData.set(undefined) // this triggers refreshJobs
            else this.refreshJobs()
            return true
        } catch (error) {
            this.notificationsService.showError(`Failed to discard render job: ${(error as Error).message}`)
            this.logger.error("Failed to discard render job", error)
            this.refreshJobs()
            return false
        } finally {
            this.$deletingRenderJob.set(false)
        }
    }
}
